import { UQC, uqcLabel } from '../enums/uqc.js'
import { ABC_OPTIONS } from '../planning/abc.js'
import { APPROVAL_STATUSES, VENDOR_MEASURE_OPTIONS, VENDOR_TYPES } from '../planning/vendor.js'
import { KICKOFF_ROLE_OPTIONS } from '../planning/kickoff.js'
import { defineMaster, m, type MasterDef } from './types.js'

const NAME_CODE = ['name', 'code'] as const

/* ---- Items and materials ------------------------------------------------ */

export const itemMaster = defineMaster({
  key: 'item', route: 'items', group: 'Items and materials',
  label: 'Item', labelPlural: 'Items',
  blurb: 'Everything you buy, hold or install — with its unit, HSN code and tax rate.',
  table: 'item', searchColumns: ['name', 'code', 'hsn_sac_code'], orderBy: 'code',
  columns: [
    { key: 'code', label: 'Code', type: 'code', width: 150 },
    { key: 'name', label: 'Name' },
    { key: 'unit', label: 'Unit', width: 100 },
    { key: 'hsn_sac_code', label: 'HSN / SAC', type: 'code', width: 130 },
    { key: 'tax_pct', label: 'Tax %', type: 'rate', width: 100, align: 'right' },
    { key: 'reorder_point', label: 'Reorder point', type: 'qty', width: 140, align: 'right' },
    // docs/24 §3.2 — on the list, so which materials are still unclassified
    // can be seen across the master rather than opened one at a time.
    { key: 'abc_class', label: 'Class', width: 90 },
    { key: 'lead_time_days', label: 'Lead time (days)', width: 150, align: 'right' },
  ],
  fields: [
    m.text('code', 'Item code', true), m.text('name', 'Name', true),
    m.text('unit', 'Unit', true), m.look('category_id', 'Category', 'item_category'),
    // The seven the table's own check constraint allows. `TOOL` used to be
    // offered here and is not one of them, so choosing it failed on save.
    m.choice('type', 'Kind', [
      ['MATERIAL', 'Material'], ['LABOUR', 'Labour'], ['SUBCONTRACT', 'Subcontract'],
      ['SERVICE', 'Service'], ['SPARE', 'Spare'], ['CHARGE', 'Charge'], ['EXPENSE', 'Expense'],
    ], false),
    m.text('hsn_sac_code', 'HSN / SAC code'), m.num('tax_pct', 'Tax %', 'rate'),
    // docs/15 R-8.6 — the Stock Purchase Planner is planning against these.
    { ...m.num('reorder_point', 'Reorder point', 'qty'),
      hint: 'When free stock falls under this, the item appears in the Stock Purchase Planner.' },
    { ...m.num('reorder_qty', 'Reorder quantity', 'qty'),
      hint: 'How much to buy when it does. Left empty, the planner suggests the shortfall.' },
    m.look('default_warehouse_id', 'Usually stocked at', 'warehouse'),
    /*
      docs/24 §3.2 — the class decides how early a material is chased, and the
      lead time turns "needed on the 14th" into "ordered by the 1st". Both are
      left empty to follow the category, and a class alone implies a lead
      time from the company's planning settings.
    */
    { ...m.choice('abc_class', 'ABC-S class', ABC_OPTIONS, false),
      hint: 'Left empty it follows the category. The ABC analysis report suggests one from value and lead time.' },
    { ...m.num('lead_time_days', 'Lead time (days)'),
      hint: 'From order to arrival. Left empty it follows the category, then the class.' },
    { ...m.num('safety_stock', 'Safety stock', 'qty'),
      hint: 'Kept back: the requirement plan does not count this much of what is on hand as free.' },
    m.area('description', 'Description'),
  ],
  softDelete: true,
} as const)

export const itemCategory = defineMaster({
  key: 'item_category', route: 'item_categories', group: 'Items and materials',
  label: 'Item category', labelPlural: 'Item categories',
  blurb: 'Group items so reports and dashboards can total by category.',
  table: 'item_category', searchColumns: [...NAME_CODE],
  columns: [
    { key: 'code', label: 'Code', type: 'code', width: 150 },
    { key: 'name', label: 'Name' },
    { key: 'hsn_sac_code', label: 'Default HSN / SAC', type: 'code', width: 180 },
    // The whole argument for putting the flag on the category is that it is
    // set once by somebody who understands the materials. Auditing that
    // decision cannot mean opening each category in turn.
    { key: 'billable_default', label: 'Charged to the customer?', width: 200 },
    { key: 'allow_excess_purchase', label: 'Excess purchase', width: 160 },
    { key: 'allow_excess_receipt', label: 'Excess receipt', width: 160 },
  ],
  fields: [
    /*
      Primary or secondary, in the customer's own words.

      The people keying a receipt cannot classify five hundred fittings from
      memory, so the classification is made once, here, by whoever sets the
      category up — and every item in it inherits it
      (docs/video-notes/06 §2).
    */
    m.choice('billable_default', 'Are these charged to the customer?', [
      ['FOLLOW', 'Decide on each line'],
      ['BILLABLE', 'Billable — primary'],
      ['NON_BILLABLE', 'Non billable — secondary support material'],
    ], false),
    m.text('code', 'Code'), m.text('name', 'Name', true),
    m.look('parent_id', 'Parent category', 'item_category'),
    m.text('hsn_sac_code', 'Default HSN / SAC'),
    // Where a BOQ import prices labour but does not name the item it is
    // booked to. The `default_labour_item` setting is the company-wide
    // fallback under this one.
    { ...m.look('labour_item_id', 'Labour booked to', 'item'),
      hint: 'Used when an imported estimation row carries a labour rate but no labour item.' },
    // docs/15 R-5.3 / R-5.4 — the tenant switch permits excess at all; the
    // category is where it is actually tolerated, and where it is not.
    { ...m.choice('allow_excess_purchase', 'Buying more than was requested', [
      ['FOLLOW', 'Follow the company setting'],
      ['ALLOW', 'Allowed for this category'],
      ['REFUSE', 'Never allowed for this category'],
    ], false),
    hint: 'Only ever allowed when the company setting permits excess at all.' },
    { ...m.choice('allow_excess_receipt', 'Receiving more than was ordered', [
      ['FOLLOW', 'Follow the company setting'],
      ['ALLOW', 'Allowed for this category'],
      ['REFUSE', 'Never allowed for this category'],
    ], false),
    hint: 'The same, for goods receipts.' },
    { ...m.num('excess_tolerance_pct', 'Excess tolerated up to (%)', 'rate'),
      hint: 'Above this the excess needs an approval of its own, not a tick in a dialog.' },
    // docs/24 §3.2 — the default every item in the category starts from.
    { ...m.choice('abc_class', 'Default ABC-S class', ABC_OPTIONS, false),
      hint: 'Items with no class of their own take this one.' },
    { ...m.num('lead_time_days', 'Default lead time (days)'),
      hint: 'Items with no lead time of their own take this one.' },
    // docs/24 §3.9 — cable and pipe are cut; a tank is not.
    { ...m.num('allowed_wastage_pct', 'Wastage allowed (%)', 'rate'),
      hint: 'Wastage above this share of what was issued needs an approval on the consumption record.' },
    m.area('description', 'Description'),
  ],
  softDelete: true,
} as const)

export const unitMaster = defineMaster({
  key: 'unit', route: 'units', group: 'Items and materials',
  label: 'Unit', labelPlural: 'Units',
  blurb: 'Units of measure, and how many decimal places each one is counted to.',
  table: 'unit', searchColumns: [...NAME_CODE], orderBy: 'code',
  columns: [
    { key: 'code', label: 'Code', type: 'code', width: 140 },
    { key: 'name', label: 'Name' },
    { key: 'decimals', label: 'Decimals', width: 120, align: 'right' },
    // On the list, not only the form: the whole point of holding it is being
    // able to see across the master which units cannot be filed yet.
    { key: 'uqc', label: 'GST unit (UQC)', width: 200 },
  ],
  fields: [
    m.text('code', 'Code', true), m.text('name', 'Name', true),
    m.num('decimals', 'Decimal places'),
    {
      ...m.choice('uqc', 'GST unit (UQC)', UQC.map((u) => [u.code, uqcLabel(u.code)]), false),
      hint: 'What GSTR-1 and the e-invoice report this unit as. OTH-OTHERS for a unit'
        + ' the GST list has no code for — a trip, a day, a lump sum.',
    },
  ],
  softDelete: true,
} as const)

export const brandMaster = defineMaster({
  key: 'brand', route: 'brands', group: 'Items and materials',
  label: 'Brand', labelPlural: 'Brands',
  blurb: 'Approved makes. A purchase outside the approved list raises an exception.',
  table: 'brand', searchColumns: ['name', 'manufacturer'],
  columns: [
    { key: 'name', label: 'Brand' },
    { key: 'manufacturer', label: 'Manufacturer' },
  ],
  fields: [m.text('name', 'Brand', true), m.text('manufacturer', 'Manufacturer'), m.area('notes', 'Notes')],
  softDelete: true,
} as const)

/* ---- Tools -------------------------------------------------------------- */

export const toolCategory = defineMaster({
  key: 'tool_category', route: 'tool_categories', group: 'Tools',
  label: 'Tool category', labelPlural: 'Tool categories',
  blurb: 'How tools are grouped for issue and availability reporting.',
  table: 'tool_category', searchColumns: [...NAME_CODE],
  columns: [{ key: 'code', label: 'Code', type: 'code', width: 150 }, { key: 'name', label: 'Name' }],
  fields: [m.text('code', 'Code'), m.text('name', 'Name', true), m.area('description', 'Description')],
  softDelete: true,
} as const)

export const toolUnitMaster = defineMaster({
  key: 'tool_unit', route: 'tool_units', group: 'Tools',
  label: 'Tool unit', labelPlural: 'Tool units',
  blurb: 'An individual, serialised tool. Not a unit of measure — this is the physical drill.',
  table: 'tool_unit', searchColumns: ['identifier', 'serial_no', 'model_no'], orderBy: 'identifier',
  columns: [
    { key: 'identifier', label: 'Identifier', type: 'code', width: 170 },
    { key: 'make', label: 'Make', width: 160 },
    { key: 'model_no', label: 'Model', width: 160 },
    { key: 'condition', label: 'Condition', width: 150 },
    { key: 'warranty_upto', label: 'Warranty to', type: 'date', width: 140 },
  ],
  fields: [
    m.text('identifier', 'Identifier', true), m.look('item_id', 'Tool', 'item'),
    m.look('tool_category_id', 'Category', 'tool_category'),
    m.text('serial_no', 'Serial no'), m.text('make', 'Make'), m.text('model_no', 'Model no'),
    m.date('purchase_date', 'Purchased on'), m.date('warranty_upto', 'Warranty until'),
    m.choice('condition', 'Condition', [
      ['GOOD', 'Good'], ['UNDER_REPAIR', 'Under repair'], ['SCRAPPED', 'Scrapped'], ['LOST', 'Lost'],
    ]),
    m.look('warehouse_id', 'Held at', 'warehouse'),
  ],
  softDelete: true,
} as const)

/* ---- Parties ------------------------------------------------------------ */

const partyColumns = [
  { key: 'code', label: 'Code', type: 'code' as const, width: 140 },
  { key: 'name', label: 'Name' },
  { key: 'gstin', label: 'GSTIN', type: 'code' as const, width: 190 },
  { key: 'state_code', label: 'State', width: 90 },
]
const partyFields = [
  m.text('code', 'Code'), m.text('name', 'Name', true),
  m.text('legal_name', 'Legal name', false, 'full'),
  m.text('gstin', 'GSTIN'), m.text('pan_no', 'PAN'),
  m.text('state_code', 'State code'), m.num('credit_period_days', 'Credit period (days)'),
  m.text('email', 'Email'), m.text('mobile', 'Mobile'),
]

/*
  What a buyer checks before trusting a supplier (docs/24 §3.6).

  The notes list them as part of the evaluation: "compliance and
  documentation — like e-way bill for material traders, like licences and PF
  and ESI for construction". Held on the record so the evaluation can measure
  them rather than ask, and so a blocked supplier cannot be ordered from.
*/
const vendorColumns = [
  ...partyColumns,
  { key: 'approval_status', label: 'Approval', width: 140 },
]
const vendorFields = [
  ...partyFields,
  { ...m.choice('approval_status', 'Approval status', APPROVAL_STATUSES, true),
    hint: 'Blocked cannot be ordered from. Provisional can, with an approval on each order.' },
  m.choice('vendor_type', 'Kind of vendor', VENDOR_TYPES, false),
  { ...m.num('lead_time_days', 'Usual lead time (days)'),
    hint: 'How long they usually take from order to delivery.' },
  m.text('msme_no', 'MSME / Udyam no'),
  { ...m.flag('eway_bill_capable', 'Raises e-way bills', 'For a material trader: goods arrive with an e-way bill.') },
  m.text('trade_licence_no', 'Trade licence no'),
  m.date('licence_valid_until', 'Licence valid until'),
  m.text('pf_registration_no', 'PF registration no'),
  m.text('esi_registration_no', 'ESI registration no'),
  m.text('quality_certification', 'Quality certification'),
]

export const customerMaster = defineMaster({
  key: 'customer', route: 'customers', group: 'Parties',
  label: 'Customer', labelPlural: 'Customers',
  blurb: 'Who you bill. Their GSTIN decides whether a sale is interstate.',
  table: 'party', searchColumns: ['name', 'code', 'gstin'], fixed: { is_customer: true },
  columns: partyColumns, fields: partyFields, softDelete: true,
} as const)

export const supplierMaster = defineMaster({
  key: 'supplier', route: 'suppliers', group: 'Parties',
  label: 'Supplier', labelPlural: 'Suppliers',
  blurb: 'Who you buy materials from — with their approval status, registrations and licences.',
  table: 'party', searchColumns: ['name', 'code', 'gstin'], fixed: { is_supplier: true },
  columns: vendorColumns, fields: vendorFields, softDelete: true,
} as const)

export const subcontractorMaster = defineMaster({
  key: 'subcontractor', route: 'subcontractors', group: 'Parties',
  label: 'Subcontractor', labelPlural: 'Subcontractors',
  blurb: 'Who does the installation work — with their licences, PF and ESI registrations.',
  table: 'party', searchColumns: ['name', 'code', 'gstin'], fixed: { is_subcontractor: true },
  columns: vendorColumns, fields: vendorFields, softDelete: true,
} as const)

/* ---- Addresses and contacts -------------------------------------------- */

/**
 * An address is its own record, not a blob on a party.
 *
 * Every priced document asks for one — deliver to, bill to, dispatch from —
 * and until this master existed there was no screen anywhere that could create
 * one, so a purchase order could be started and never finished.
 */
export const addressMaster = defineMaster({
  key: 'address', route: 'addresses', group: 'Addresses and contacts',
  label: 'Address', labelPlural: 'Addresses',
  blurb: 'Sites, registered offices and supplier premises. Documents deliver to, bill to and dispatch from these.',
  table: 'address', searchColumns: ['label', 'line1', 'line2', 'city', 'postal_code', 'gstin'],
  orderBy: 'label',
  columns: [
    { key: 'label', label: 'Name', width: 220 },
    { key: 'line1', label: 'Address' },
    { key: 'city', label: 'City', width: 160 },
    { key: 'state_code', label: 'State', width: 90 },
    { key: 'gstin', label: 'GSTIN', type: 'code', width: 190 },
    { key: 'is_default', label: 'Default', type: 'boolean', width: 110 },
  ],
  fields: [
    m.text('label', 'Name for this address', true, 'full'),
    m.choice('owner_type', 'Belongs to', [
      ['COMPANY', 'Us — one of our own offices or stores'],
      ['PARTY', 'A customer, supplier or subcontractor'],
      ['PROJECT', 'A project site'],
      ['WAREHOUSE', 'A warehouse'],
    ], false),
    m.look('owner_id', 'Which one', 'party'),
    m.text('line1', 'Address line 1', false, 'full'),
    m.text('line2', 'Address line 2', false, 'full'),
    m.text('city', 'City'), m.text('state', 'State'), m.text('state_code', 'State code'),
    m.text('postal_code', 'Postal code'), m.text('country', 'Country'),
    m.text('gstin', 'GSTIN'),
    m.flag('is_default', 'Use this one by default'),
  ],
  softDelete: true,
} as const)

export const contactMaster = defineMaster({
  key: 'contact', route: 'contacts', group: 'Addresses and contacts',
  label: 'Contact', labelPlural: 'Contacts',
  blurb: 'The people documents are addressed to — site engineers, supplier salesmen, accounts staff.',
  table: 'contact', searchColumns: ['name', 'email', 'mobile', 'designation'],
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'designation', label: 'Designation', width: 200 },
    { key: 'email', label: 'Email', width: 240 },
    { key: 'mobile', label: 'Mobile', width: 160 },
  ],
  fields: [
    m.text('name', 'Name', true),
    m.text('designation', 'Designation'),
    m.choice('owner_type', 'Belongs to', [
      ['COMPANY', 'Us'],
      ['PARTY', 'A customer, supplier or subcontractor'],
      ['PROJECT', 'A project'],
      ['WAREHOUSE', 'A warehouse'],
    ], false),
    m.look('owner_id', 'Which one', 'party'),
    m.text('email', 'Email'), m.text('mobile', 'Mobile'), m.text('landline', 'Landline'),
  ],
  softDelete: true,
} as const)

/* ---- Commercial --------------------------------------------------------- */

/** docs/24 §3.6 — the nine things a supplier is weighed on, and how much each counts. */
export const vendorCriterionMaster = defineMaster({
  key: 'vendor_criterion', route: 'vendor_criteria', group: 'Commercial',
  label: 'Vendor evaluation criterion', labelPlural: 'Vendor evaluation criteria',
  blurb: 'What a supplier is evaluated on, the weight of each, and which the system measures itself.',
  table: 'vendor_criterion', searchColumns: ['name', 'code'], orderBy: 'seq',
  columns: [
    { key: 'code', label: 'Code', type: 'code', width: 140 },
    { key: 'name', label: 'Criterion' },
    { key: 'weight', label: 'Weight %', type: 'rate', width: 120, align: 'right' },
    { key: 'measure', label: 'Scored', width: 260 },
  ],
  fields: [
    m.text('code', 'Code'), m.text('name', 'Criterion', true),
    { ...m.num('weight', 'Weight (%)', 'rate', true),
      hint: 'The weights of the criteria on one evaluation must add to 100.' },
    { ...m.choice('measure', 'How it is scored', VENDOR_MEASURE_OPTIONS, true),
      hint: 'A measured criterion is pre-filled from receipts, inspections and quotations; the evaluator can still change it.' },
    { ...m.num('seq', 'Order', 'number', true), hint: 'Where it sits on an evaluation: 1 first.' },
    m.area('description', 'What it means'),
  ],
  softDelete: true,
} as const)

export const supplierBillingTerm = defineMaster({
  key: 'supplier_billing_term', route: 'sup_billing_terms', group: 'Commercial',
  label: 'Supplier billing term', labelPlural: 'Supplier billing terms',
  blurb: 'How a subcontract is billed — the split between supply and installation.',
  table: 'supplier_billing_term', searchColumns: ['name'],
  columns: [
    { key: 'name', label: 'Term' },
    { key: 'supply_pct', label: 'Supply %', type: 'rate', width: 130, align: 'right' },
    { key: 'installation_pct', label: 'Installation %', type: 'rate', width: 150, align: 'right' },
  ],
  fields: [
    m.text('name', 'Name', true),
    m.num('supply_pct', 'Supply %', 'rate'), m.num('installation_pct', 'Installation %', 'rate'),
    m.area('description', 'Description'),
  ],
  softDelete: true,
} as const)

export const taxRateMaster = defineMaster({
  key: 'tax_rate', route: 'taxes', group: 'Commercial',
  label: 'Tax', labelPlural: 'Taxes',
  blurb: 'GST rates, and whether each applies within the state or across it.',
  table: 'tax_rate', searchColumns: ['name', 'tax_type'],
  columns: [
    { key: 'name', label: 'Tax' },
    { key: 'tax_type', label: 'Type', width: 130 },
    { key: 'rate_pct', label: 'Rate %', type: 'rate', width: 120, align: 'right' },
    { key: 'is_interstate', label: 'Interstate', type: 'boolean', width: 130 },
  ],
  fields: [
    m.text('name', 'Name', true),
    m.choice('tax_type', 'Type', [['GST', 'GST'], ['CGST', 'CGST'], ['SGST', 'SGST'], ['IGST', 'IGST'], ['TDS', 'TDS']]),
    m.num('rate_pct', 'Rate %', 'rate', true),
    m.flag('is_interstate', 'Applies interstate'),
  ],
  softDelete: true,
} as const)

export const invoiceAdjustmentMaster = defineMaster({
  key: 'invoice_adjustment', route: 'invoice_adjustments', group: 'Commercial',
  label: 'Invoice adjustment', labelPlural: 'Invoice adjustments',
  blurb: 'Standing deductions and additions — retention, freight, penalties.',
  table: 'invoice_adjustment', searchColumns: ['name'],
  columns: [
    { key: 'name', label: 'Adjustment' },
    { key: 'direction', label: 'Direction', width: 150 },
    { key: 'default_pct', label: 'Default %', type: 'rate', width: 130, align: 'right' },
    { key: 'taxable', label: 'Taxable', type: 'boolean', width: 110 },
  ],
  fields: [
    m.text('name', 'Name', true),
    m.choice('direction', 'Direction', [['DEDUCTION', 'Deduction'], ['ADDITION', 'Addition']]),
    m.num('default_pct', 'Default %', 'rate'), m.flag('taxable', 'Taxable'),
  ],
  softDelete: true,
} as const)

export const jobTypeMaster = defineMaster({
  key: 'job_type', route: 'job_types', group: 'Commercial',
  label: 'Job type', labelPlural: 'Job types',
  blurb: 'The trade a document belongs to. Every dashboard splits by this.',
  table: 'job_type', searchColumns: [...NAME_CODE],
  columns: [{ key: 'code', label: 'Code', type: 'code', width: 150 }, { key: 'name', label: 'Job type' }],
  fields: [m.text('code', 'Code'), m.text('name', 'Name', true)],
  softDelete: true,
} as const)

export const taxUnitMaster = defineMaster({
  key: 'tax_unit', route: 'tax_units', group: 'Commercial',
  label: 'Tax unit', labelPlural: 'Tax units',
  blurb: 'A GSTIN registration. Documents are issued under one of these.',
  table: 'tax_unit', searchColumns: ['name', 'gstin'],
  columns: [
    { key: 'name', label: 'Tax unit' },
    { key: 'gstin', label: 'GSTIN', type: 'code', width: 200 },
    { key: 'state_code', label: 'State', width: 100 },
  ],
  fields: [
    m.text('name', 'Name', true), m.text('gstin', 'GSTIN', true),
    m.text('state_code', 'State code'),
  ],
  softDelete: true,
} as const)

/* ---- People and teams --------------------------------------------------- */

export const employeeMaster = defineMaster({
  key: 'employee', route: 'employees', group: 'People and teams',
  label: 'Employee', labelPlural: 'Employees',
  blurb: 'People who raise, approve and are issued things.',
  table: 'employee', searchColumns: ['name', 'code', 'email'],
  columns: [
    { key: 'code', label: 'Code', type: 'code', width: 140 },
    { key: 'name', label: 'Name' },
    { key: 'designation', label: 'Designation', width: 200 },
    { key: 'email', label: 'Email', width: 240 },
  ],
  fields: [
    m.text('code', 'Code'), m.text('name', 'Name', true),
    m.text('designation', 'Designation'), m.text('email', 'Email'), m.text('mobile', 'Mobile'),
    // The role master used to be a list nobody could be put on. Permissions
    // and named approval steps both read this (docs/15 R-10.1).
    { ...m.look('role_id', 'Role', 'app_role'),
      hint: 'What this person may do, and which approval steps they may sign. Left empty, their sign-in role decides.' },
  ],
  softDelete: true,
} as const)

export const projectRoleMaster = defineMaster({
  key: 'project_role', route: 'project_roles', group: 'People and teams',
  label: 'Project role', labelPlural: 'Project roles',
  blurb: 'What someone does on a project — site engineer, project manager, storekeeper.',
  table: 'project_role', searchColumns: ['name'],
  columns: [{ key: 'name', label: 'Role' }, { key: 'description', label: 'Description' }],
  fields: [m.text('name', 'Name', true), m.area('description', 'Description')],
  softDelete: true,
} as const)

export const teamMaster = defineMaster({
  key: 'team', route: 'teams', group: 'People and teams',
  label: 'Team', labelPlural: 'Teams',
  blurb: 'Project teams and billing teams, and who leads each.',
  table: 'team', searchColumns: ['name'],
  columns: [
    { key: 'name', label: 'Team' },
    { key: 'kind', label: 'Kind', width: 140 },
  ],
  fields: [
    m.text('name', 'Name', true),
    m.choice('kind', 'Kind', [['PROJECT', 'Project team'], ['BILLING', 'Billing team']]),
    m.look('lead_employee_id', 'Led by', 'employee'),
    m.area('description', 'Description'),
  ],
  softDelete: true,
} as const)

export const warehouseMaster = defineMaster({
  key: 'warehouse', route: 'warehouses', group: 'People and teams',
  label: 'Warehouse', labelPlural: 'Warehouses',
  blurb: 'Where stock is held, including site stores.',
  table: 'warehouse', searchColumns: [...NAME_CODE],
  columns: [{ key: 'code', label: 'Code', type: 'code', width: 150 }, { key: 'name', label: 'Warehouse' }],
  fields: [m.text('code', 'Code'), m.text('name', 'Name', true)],
  softDelete: true,
} as const)

/** docs/24 §3.8 — a rack or bin inside a warehouse: where the storekeeper walks to. */
export const storageBinMaster = defineMaster({
  key: 'storage_bin', route: 'storage_bins', group: 'People and teams',
  label: 'Storage bin', labelPlural: 'Storage bins',
  blurb: 'Racks and bins inside a warehouse. A receipt says which bin its material went into.',
  table: 'storage_bin', searchColumns: ['code', 'name', 'zone'], orderBy: 'code',
  columns: [
    { key: 'code', label: 'Bin', type: 'code', width: 150 },
    { key: 'name', label: 'Description' },
    { key: 'zone', label: 'Zone', width: 160 },
  ],
  fields: [
    m.look('warehouse_id', 'Warehouse', 'warehouse', true),
    { ...m.text('code', 'Bin code', true), hint: 'As painted on the rack: R2-B3, YARD-PIPES.' },
    m.text('name', 'Description'),
    { ...m.text('zone', 'Zone'), hint: 'A part of the store: covered, open yard, cold room.' },
  ],
  softDelete: true,
} as const)

export const currencyMaster = defineMaster({
  key: 'currency', route: 'currencies', group: 'Commercial',
  label: 'Currency', labelPlural: 'Currencies',
  blurb: 'The currencies your business trades in, and how each one is written.',
  table: 'currency', searchColumns: ['code', 'name'], orderBy: 'code',
  columns: [
    { key: 'code', label: 'Code', type: 'code', width: 110 },
    { key: 'name', label: 'Currency' },
    { key: 'symbol', label: 'Symbol', width: 100 },
    { key: 'grouping', label: 'Grouping', width: 150 },
    { key: 'is_base', label: 'Base', type: 'boolean', width: 100 },
  ],
  fields: [
    m.text('code', 'Code', true), m.text('name', 'Name', true),
    m.text('symbol', 'Symbol'),
    m.choice('grouping', 'Digit grouping', [['INDIAN', 'Indian — 1,23,456.00'], ['WESTERN', 'Western — 123,456.00']], false),
    m.num('scale', 'Decimal places'),
    m.text('major_unit', 'Major unit'), m.text('minor_unit', 'Minor unit'),
    m.flag('is_base', 'This is the base currency'),
  ],
  softDelete: true,
} as const)

export const roleMaster = defineMaster({
  key: 'app_role', route: 'roles', group: 'People and teams',
  label: 'Role', labelPlural: 'Roles',
  blurb: 'The named roles you assign to people. Each one carries its own permissions.',
  table: 'app_role', searchColumns: ['name', 'code'], orderBy: 'name',
  columns: [
    { key: 'code', label: 'Code', type: 'code', width: 190 },
    { key: 'name', label: 'Role' },
    { key: 'approves', label: 'May approve', type: 'boolean', width: 140 },
    { key: 'is_builtin', label: 'Built in', type: 'boolean', width: 110 },
  ],
  fields: [
    m.text('code', 'Code', true, 'half'),
    m.text('name', 'Name', true),
    m.flag('approves', 'People in this role may approve documents by default'),
    m.area('description', 'What this role is for'),
  ],
  softDelete: true,
} as const)

/**
 * The shape of the RA bill annexure (docs/video-notes/06 §6).
 *
 * "One person will ask in row, one will ask in column format… you can change
 * the variables there and use those variables for the client." The annexure is
 * the thing a customer's quantity surveyor either accepts or sends back, and a
 * fixed twelve-column sheet suits some customers and not others — the fallback
 * being to rebuild it in Excel every month.
 *
 * A format names the columns it wants, in the order it wants them, and whether
 * a BOQ line is a row or a column. The one whose customer matches wins; failing
 * that the default; failing that every column, which is what the sheet was
 * before this existed.
 */
export const raBillFormatMaster = defineMaster({
  key: 'ra_bill_format', route: 'ra_bill_formats', group: 'Documents',
  label: 'RA bill format', labelPlural: 'RA bill formats',
  blurb: 'How the running account annexure is laid out, per customer.',
  table: 'ra_bill_format', searchColumns: ['name', 'code'], orderBy: 'name',
  columns: [
    { key: 'code', label: 'Code', type: 'code', width: 150 },
    { key: 'name', label: 'Name' },
    { key: 'layout', label: 'Laid out', width: 150 },
    { key: 'is_default', label: 'Default', type: 'boolean', width: 110 },
  ],
  fields: [
    m.text('code', 'Code'), m.text('name', 'Name', true),
    { ...m.look('customer_id', 'For this customer', 'customer'),
      hint: 'Leave it empty and this format is available to every customer.' },
    m.choice('layout', 'Laid out', [
      ['ROW', 'One row per BOQ line'],
      ['COLUMN', 'One column per BOQ line — the sheet turned on its side'],
    ], false),
    { ...m.text('columns_csv', 'Columns, in order', false, 'full'),
      hint:
        'Comma-separated, from: boqCode, description, unit, boqQty, rate, previousQty, currentQty, '
        + 'cumulativeQty, previousValue, currentValue, cumulativeValue, challans. '
        + 'Left empty, all of them are used.' },
    m.flag('is_default', 'Use this when a customer has no format of their own'),
  ],
  softDelete: true,
} as const)

/* ---- Drive -------------------------------------------------------------- */

export const fileCategoryMaster = defineMaster({
  key: 'file_category', route: 'file_categories', group: 'Drive',
  label: 'File category', labelPlural: 'File categories',
  blurb: 'How files are labelled on documents and in the project drive.',
  table: 'file_category', searchColumns: ['name'],
  columns: [
    { key: 'name', label: 'Category' },
    { key: 'access_to', label: 'Visible to', width: 200 },
    { key: 'kickoff_role', label: 'Kick-off pack', width: 220 },
    { key: 'colour', label: 'Colour', width: 130 },
  ],
  fields: [
    m.text('name', 'Name', true),
    m.choice('access_to', 'Visible to', [['EVERYONE', 'Everyone'], ['ROLE', 'One role only']], false),
    m.look('role_id', 'Which role', 'app_role'),
    // docs/24 §3.1 — which part of the kick-off pack files in this category are.
    { ...m.choice('kickoff_role', 'Part of the kick-off pack', KICKOFF_ROLE_OPTIONS, false),
      hint: 'The project readiness check looks for a file in each part of the pack.' },
    m.text('colour', 'Colour'),
  ],
  softDelete: true,
} as const)

export const collectionTypeMaster = defineMaster({
  key: 'collection_type', route: 'collection_types', group: 'Drive',
  label: 'Collection type', labelPlural: 'Collection types',
  blurb: 'The kinds of collection a project drive may hold — drawings, certificates, correspondence.',
  table: 'collection_type', searchColumns: ['name'],
  columns: [
    { key: 'name', label: 'Collection type' },
    { key: 'access_to', label: 'Visible to', width: 200 },
    { key: 'document_exchange', label: 'Shared outside', type: 'boolean', width: 160 },
    { key: 'colour', label: 'Colour', width: 130 },
  ],
  fields: [
    m.text('name', 'Name', true),
    m.choice('access_to', 'Visible to', [['EVERYONE', 'Everyone'], ['ROLE', 'One role only']], false),
    m.look('role_id', 'Which role', 'app_role'),
    m.flag('document_exchange', 'May be shared with an outside party'),
    m.text('colour', 'Colour'),
  ],
  softDelete: true,
} as const)

/* ---- Documents ---------------------------------------------------------- */

export const termsTemplateMaster = defineMaster({
  key: 'terms_template', route: 'terms_templates', group: 'Documents',
  label: 'Terms template', labelPlural: 'Terms templates',
  blurb: 'Standard terms and conditions, pickable on any document that prints.',
  table: 'terms_template', searchColumns: ['name'],
  columns: [
    { key: 'name', label: 'Template' },
    { key: 'doc_type', label: 'Used on', width: 200 },
    { key: 'is_default', label: 'Default', type: 'boolean', width: 120 },
  ],
  fields: [
    m.text('name', 'Name', true), m.text('doc_type', 'Used on document type'),
    m.flag('is_default', 'Use by default'), m.area('body', 'Terms'),
  ],
  softDelete: true,
} as const)

export const MASTER_DEFS = [
  itemMaster, itemCategory, unitMaster, brandMaster,
  toolCategory, toolUnitMaster,
  customerMaster, supplierMaster, subcontractorMaster,
  addressMaster, contactMaster,
  vendorCriterionMaster, supplierBillingTerm, taxRateMaster, invoiceAdjustmentMaster, jobTypeMaster, taxUnitMaster,
  currencyMaster,
  employeeMaster, roleMaster, projectRoleMaster, teamMaster, warehouseMaster, storageBinMaster,
  fileCategoryMaster, collectionTypeMaster,
  termsTemplateMaster, raBillFormatMaster,
] as const satisfies readonly MasterDef[]

const byKey = new Map<string, MasterDef>(MASTER_DEFS.map((d) => [d.key, d]))
const byRoute = new Map<string, MasterDef>(MASTER_DEFS.map((d) => [d.route, d]))
export const getMasterDef = (key: string): MasterDef | undefined => byKey.get(key)
export const getMasterDefByRoute = (route: string): MasterDef | undefined => byRoute.get(route)
export const requireMasterDef = (key: string): MasterDef => {
  const d = byKey.get(key)
  if (!d) throw new Error(`Unknown master: ${key}`)
  return d
}
